import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";

type Campaign = { id: string; name: string };

type Ctx = {
  campaigns: Campaign[];
  campaign: Campaign | null;
  setCampaign: (c: Campaign | null) => void;
  refreshCampaigns: () => Promise<void>;
  loading: boolean;
  error: string | null;
};

const CampaignContext = createContext<Ctx | undefined>(undefined);
const LAST = "gc:lastSelectedCampaignId";

export function CampaignProvider({ children }: { children: React.ReactNode }) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaign, setCampaignState] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const booted = useRef(false);

  const setCampaign = (c: Campaign | null) => {
    setCampaignState(c);
    try {
      if (c?.id) localStorage.setItem(LAST, c.id);
      else localStorage.removeItem(LAST);
    } catch {}
  };

  // Minimal provider load (fine if this returns empty; the switcher will self-hydrate)
  const refreshCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data, error } = await supabase
        .from("campaign_users")
        .select("campaigns(id,name)")
        .eq("invitation_status", "accepted");

      if (error) throw error;

      const list: Campaign[] = Array.from(
        new Map(
          (data ?? [])
            .map((r: any) => r.campaigns)
            .filter((c: any) => c?.id)
            .map((c: Campaign) => [c.id, c])
        ).values()
      ).sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      setCampaigns(list);

      const last = (() => { try { return localStorage.getItem(LAST); } catch { return null; } })();
      const fromLast = list.find((c) => c.id === last);
      if (fromLast) setCampaignState(fromLast);
      else if (list.length === 1) setCampaignState(list[0]);
      else setCampaignState((prev) =>
        prev && list.find((c) => c.id === prev.id) ? prev : null
      );
    } catch (e: any) {
      setError(e?.message ?? "Failed to load campaigns");
      setCampaigns([]);
      setCampaign(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;

    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) await refreshCampaigns();
      else setTimeout(refreshCampaigns, 300);
      setLoading(false);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session?.user) refreshCampaigns();
      else { setCampaigns([]); setCampaign(null); }
    });
    return () => sub?.subscription?.unsubscribe();
  }, []);

  const value = useMemo(
    () => ({ campaigns, campaign, setCampaign, refreshCampaigns, loading, error }),
    [campaigns, campaign, loading, error]
  );

  return <CampaignContext.Provider value={value}>{children}</CampaignContext.Provider>;
}

export function useCampaignContext() {
  const ctx = useContext(CampaignContext);
  if (!ctx) throw new Error("useCampaignContext must be used within CampaignProvider");
  return ctx;
}
